/**
 * Main Layout component for trAInedup.ai
 * Provides consistent navigation and structure across the application
 */

import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  BookOpen,
  ChevronDown,
  CreditCard,
  Facebook,
  Gauge,
  HelpCircle,
  Home,
  Instagram,
  FileText,
  LogOut,
  Menu,
  MessageCircle,
  Shield,
  User,
  UsersRound,
  X,
  Youtube,
  Zap,
  DollarSign,
  ClipboardList,
  Map,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useSubscription } from '../../contexts/SubscriptionContext';
import { Button } from './Button';
import LanguageSwitcher from './LanguageSwitcher';
import { UserRole } from '../../types';
import { ROUTES } from '../../utils/constants';
import { SUBSCRIPTION_TIERS } from '../../services/stripe/config';

// Chatbot for unauthenticated visitors
import SubscriptionChatbot from '../chat/SubscriptionChatbot';
import { UpgradeBanner } from './UpgradeBanner';
import { ReadinessBarWrapper } from '../gamification/ReadinessBarWrapper';
import { useGamification } from '../../hooks/useGamification';
import { XPCounter } from '../gamification/XPCounter';
import { NextStepBlock } from '../gamification/NextStepBlock';
import { getAffiliateProfile } from '../../services/firebase/affiliateProfile';
import { getUserAffiliateApplication } from '../../services/firebase/affiliates';

interface LayoutProps {
  children: React.ReactNode;
  showHeader?: boolean;
  showFooter?: boolean;
  showSidebar?: boolean;
  showUpgradeBanner?: boolean;
  showChatbot?: boolean;
}

/**
 * Main layout wrapper component
 * Includes responsive navigation, header, and footer
 */
export const Layout: React.FC<LayoutProps> = ({
  children,
  showHeader = true,
  showFooter = true,
  showSidebar = true,
  showUpgradeBanner = true,
  showChatbot = true,
}) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = React.useState(false);
  const [isSidebarExpanded, setIsSidebarExpanded] = React.useState(false);
  const [expandedSections, setExpandedSections] = React.useState<Record<string, boolean>>({});
  const [expandedMobileSections, setExpandedMobileSections] = React.useState<Record<string, boolean>>({});
  const [openFlyoutItem, setOpenFlyoutItem] = React.useState<string | null>(null);
  const [isUserMenuOpen, setIsUserMenuOpen] = React.useState(false);
  const [isAuthMenuOpen, setIsAuthMenuOpen] = React.useState(false);
  const authMenuRef = React.useRef<HTMLDivElement>(null);
  const [isReadinessCollapsed, setIsReadinessCollapsed] = React.useState(true);
  const [isXPWidgetVisible, setIsXPWidgetVisible] = React.useState(() => {
    if (typeof window === 'undefined') return true;
    const closedAt = localStorage.getItem('xp_widget_closed_at');
    if (closedAt) {
      const timeSinceClosed = Date.now() - parseInt(closedAt, 10);
      // Re-appear after 24 hours
      return timeSinceClosed > 24 * 60 * 60 * 1000;
    }
    return true;
  });
  const headerRef = React.useRef<HTMLDivElement | null>(null);
  const [headerHeight, setHeaderHeight] = React.useState(72);
  const [isDesktopViewport, setIsDesktopViewport] = React.useState(
    typeof window !== 'undefined' ? window.innerWidth >= 768 : false
  );
  const location = useLocation();
  const navigate = useNavigate();
  // Temporary flag to hide language selector
  const SHOW_LANGUAGE_SWITCHER = false;

  // Authentication state from context
  const { isAuthenticated, logout, user } = useAuth();
  const { getCurrentTier } = useSubscription();
  const { progress: gamificationProgress } = useGamification();
  
  const [isAffiliate, setIsAffiliate] = React.useState(false);

  React.useEffect(() => {
    const checkAffiliateStatus = async () => {
      if (user) {
        try {
          const profile = await getAffiliateProfile(user.uid);
          if (profile) {
            setIsAffiliate(true);
          } else {
            // Also check for pending application
            const application = await getUserAffiliateApplication(user.uid);
            if (application) {
              setIsAffiliate(true);
            }
          }
        } catch (error) {
          console.error('Error checking affiliate status:', error);
        }
      }
    };
    checkAffiliateStatus();
  }, [user]);

  const isVerified = user?.emailVerified;
  const showAuthenticatedLayout = isAuthenticated && isVerified;
  
  const handleSignOut = async () => {
    try {
      await logout();
    } finally {
      setIsMobileMenuOpen(false);
      navigate(ROUTES.home);
    }
  };

  type NavItem = {
    name: string;
    href?: string;
    icon: LucideIcon;
    expandedLabel?: string;
    children?: Array<{ name: string; href: string }>;
    onClick?: () => void;
    highlight?: boolean;
  };

  const currentTier = getCurrentTier();
  
  // Directly check user roles to ensure admin visibility is correct
  const isAdmin = React.useMemo(() => {
    return user?.roles?.includes(UserRole.ADMIN) ?? false;
  }, [user]);

  const isPaidTier = currentTier !== SUBSCRIPTION_TIERS.FREE;
  const showBillingMenu = isPaidTier || isAdmin;
  const showPricingMenu = !showBillingMenu;
  const desktopContentOffset = showSidebar ? (isSidebarExpanded ? 'md:ml-64' : 'md:ml-20') : '';

  // Navigation items for public pages (homepage redesign: minimal links)
  const publicNavItems: NavItem[] = [
    { name: 'Pricing', href: ROUTES.pricing, icon: CreditCard },
    { name: 'Blog', href: ROUTES.blog, icon: FileText },
    { name: 'Courses', href: ROUTES.courses, icon: BookOpen },
  ];

  // Navigation items for authenticated users
  const getAuthenticatedNavItems = (): NavItem[] => {
    const lastAssessmentDate = user?.lastAssessmentDate;
    const oneMonthAgo = new Date();
    oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1);
    
    const shouldHighlightAssessment = !lastAssessmentDate || new Date(lastAssessmentDate) < oneMonthAgo;
    const isTwelveMonthUser = user?.subscriptionTier === 'twelve_month_package';

    const adminNavItem: NavItem = {
      name: 'Admin',
      href: ROUTES.adminUsers,
      icon: Shield,
      children: [
        { name: 'User Management', href: ROUTES.adminUsers },
        { name: 'Analytics', href: ROUTES.adminAnalytics },
        { name: 'Assessment Review', href: '/dashboard/admin/assessments' },
        { name: 'Courses', href: ROUTES.adminCourses },
        { name: 'Affiliates', href: ROUTES.adminAffiliates },
        { name: 'Email Management', href: ROUTES.adminEmails },
        { name: 'Blog', href: ROUTES.adminBlog },
        // Removed Email Flow Visualization and Change User Flow
        { name: 'Activation Codes', href: ROUTES.adminActivationCodes },
        { name: 'Redemptions', href: ROUTES.adminActivationRedemptions },
        { name: 'Support Tickets', href: ROUTES.adminSupport },
        { name: 'System Health', href: '/dashboard/admin/health' },
        { name: 'Admin Settings', href: ROUTES.adminSettings },
      ],
    };

    const items: NavItem[] = [
      { name: 'Home', href: ROUTES.home, icon: Home },
    ];

    if (isAdmin) {
      items.unshift(adminNavItem);
    }
    
    // 12-month package users don't need the skills assessment
    if (!isTwelveMonthUser) {
      items.push({ 
        name: 'Skills Assessment', 
        href: '/skills-assessment',
        icon: ClipboardList,
        highlight: shouldHighlightAssessment
      });
    }
    
    items.push(
      { name: 'Dashboard', href: ROUTES.dashboard, icon: Gauge },
      { name: 'Blog', href: ROUTES.blog, icon: FileText },
      { name: 'Courses', href: ROUTES.courses, icon: BookOpen },
      { name: 'Learning Journey', href: ROUTES.learningJourney, icon: Map },
      { name: 'Community', href: ROUTES.community, icon: UsersRound },
    );

    if (showPricingMenu) {
      items.push({ name: 'Pricing', href: ROUTES.pricing, icon: CreditCard });
    } else if (showBillingMenu) {
      items.push({ name: 'Billing', href: `${ROUTES.dashboard}/billing`, icon: CreditCard });
    }

    if (isAffiliate) {
      items.push({ name: 'Affiliate', href: '/dashboard/affiliate', icon: DollarSign });
    }

    items.push(
      {
        name: 'AI Readiness',
        expandedLabel: 'AI Readiness Progress',
        href: '/ai-readiness',
        icon: Zap,
      },
      { name: 'Support', href: ROUTES.support, icon: HelpCircle },
      { name: 'Profile', href: ROUTES.profile, icon: User }
    );

    items.push({ name: 'Sign Out', icon: LogOut, onClick: handleSignOut });

    return items;
  };

  const authenticatedNavItems = getAuthenticatedNavItems();

  const navItems = showAuthenticatedLayout ? authenticatedNavItems : publicNavItems;

  const isReadinessPage = location.pathname.startsWith('/ai-readiness');
  const isAffiliateDashboard = location.pathname.startsWith('/dashboard/affiliate');
  const totalXP = gamificationProgress?.totalXP ?? 0;
  const readinessPercentage = gamificationProgress?.readinessPercentage ?? 0;
  const showReadinessBar = isReadinessPage || !isReadinessCollapsed;

  // Track previous XP to detect increases
  const prevXPRef = React.useRef(totalXP);

  // Show widget when XP increases
  React.useEffect(() => {
    if (totalXP > prevXPRef.current) {
      setIsXPWidgetVisible(true);
      localStorage.removeItem('xp_widget_closed_at');
    }
    prevXPRef.current = totalXP;
  }, [totalXP]);

  const handleCloseXPWidget = () => {
    setIsXPWidgetVisible(false);
    localStorage.setItem('xp_widget_closed_at', Date.now().toString());
  };

  const updateLayoutOffsets = React.useCallback(() => {
    setHeaderHeight(headerRef.current?.offsetHeight ?? 0);

    if (typeof window !== 'undefined') {
      setIsDesktopViewport(window.innerWidth >= 768);
    }
  }, []);

  React.useEffect(() => {
    updateLayoutOffsets();
    window.addEventListener('resize', updateLayoutOffsets);

    return () => {
      window.removeEventListener('resize', updateLayoutOffsets);
    };
  }, [updateLayoutOffsets]);

  React.useEffect(() => {
    updateLayoutOffsets();
  }, [isMobileMenuOpen, showReadinessBar, updateLayoutOffsets]);

  const headerOffsetStyle = showHeader
    ? { ['--header-offset' as string]: `${headerHeight}px` }
    : undefined;

  const contentPaddingTop = showHeader && !isDesktopViewport ? headerHeight : 0;

  React.useEffect(() => {
    if (!isReadinessPage) {
      setIsReadinessCollapsed(true);
    }
  }, [isReadinessPage]);

  const handleReadinessToggle = () => {
    setIsReadinessCollapsed((prev) => !prev);
  };

  // Check if current route is active
  const isActiveRoute = (href: string, children?: Array<{ href: string }>) => {
    // Special case for Home - exact match only
    if (href === ROUTES.home) {
      return location.pathname === href;
    }
    
    // Special case for Dashboard - should NOT match admin routes
    if (href === ROUTES.dashboard) {
      // Exact match or starts with /dashboard/ but NOT /dashboard/admin
      return location.pathname === href || 
        (location.pathname.startsWith(`${href}/`) && !location.pathname.startsWith('/dashboard/admin'));
    }
    
    // For items with children (like Admin), check if any child route is active
    if (children && children.length > 0) {
      const matchesChild = children.some(child => 
        location.pathname === child.href || location.pathname.startsWith(`${child.href}/`)
      );
      if (matchesChild) return true;
    }
    
    // Check if current path matches this route
    const matchesRoute = location.pathname === href || location.pathname.startsWith(`${href}/`);
    
    return matchesRoute;
  };

  const toggleSection = (name: string) => {
    setExpandedSections((prev) => ({
      ...prev,
      [name]: !prev[name],
    }));
  };

  const toggleMobileSection = (name: string) => {
    setExpandedMobileSections((prev) => ({
      ...prev,
      [name]: !prev[name],
    }));
  };

  const renderUserMenuDropdown = () => (
    <div className="absolute right-0 mt-2 w-48 bg-white border border-neutral-200 rounded-lg shadow-lg py-2 z-50">
      <Link
        to={ROUTES.profile}
        className="block px-4 py-2 text-sm text-neutral-700 hover:bg-neutral-50"
        onClick={() => setIsUserMenuOpen(false)}
      >
        Profile
      </Link>
      <Link
        to={ROUTES.dashboard}
        className="block px-4 py-2 text-sm text-neutral-700 hover:bg-neutral-50"
        onClick={() => setIsUserMenuOpen(false)}
      >
        Dashboard
      </Link>
      <button
        type="button"
        className="w-full text-left px-4 py-2 text-sm text-neutral-700 hover:bg-neutral-50"
        onClick={() => {
          setIsUserMenuOpen(false);
          handleSignOut();
        }}
      >
        Sign Out
      </button>
    </div>
  );

  // Derived user initials for the avatar
  const userInitials = React.useMemo(() => {
    const first = user?.firstName?.[0] ?? '';
    const last = user?.lastName?.[0] ?? '';
    if (first || last) return (first + last).toUpperCase();
    return user?.displayName?.[0]?.toUpperCase() ?? '?';
  }, [user]);

  const renderAuthAvatarDropdown = (position: 'above' | 'below' = 'above') => (
    <div
      className={`absolute ${position === 'above' ? 'bottom-full mb-2 left-0' : 'top-full mt-2 right-0'} w-64 bg-white border border-neutral-200 rounded-xl shadow-xl py-2 z-50`}
    >
      {/* User info header */}
      <div className="px-4 py-3 border-b border-neutral-100">
        <p className="font-montserrat font-bold text-sm text-neutral-800 truncate">
          {user?.displayName || [user?.firstName, user?.lastName].filter(Boolean).join(' ') || 'User'}
        </p>
        <p className="font-open-sans text-xs text-neutral-500 truncate">{user?.email}</p>
      </div>

      <Link
        to={ROUTES.profile}
        className="block px-4 py-2.5 text-sm text-neutral-700 hover:bg-neutral-50 transition-colors"
        onClick={() => setIsAuthMenuOpen(false)}
      >
        Profile Settings
      </Link>

      {isPaidTier && (
        <Link
          to={`${ROUTES.dashboard}/billing`}
          className="block px-4 py-2.5 text-sm text-neutral-700 hover:bg-neutral-50 transition-colors"
          onClick={() => setIsAuthMenuOpen(false)}
        >
          Billing
        </Link>
      )}

      <div className="my-1 border-t border-neutral-100" />

      <button
        type="button"
        className="w-full text-left px-4 py-2.5 text-sm text-neutral-700 hover:bg-neutral-50 transition-colors"
        onClick={() => { setIsAuthMenuOpen(false); handleSignOut(); }}
      >
        Log Out
      </button>
    </div>
  );

  React.useEffect(() => {
    setIsUserMenuOpen(false);
  }, [location.pathname, showAuthenticatedLayout]);

  // Close auth avatar menu on outside click or Escape key
  React.useEffect(() => {
    if (!isAuthMenuOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (authMenuRef.current && !authMenuRef.current.contains(e.target as Node)) {
        setIsAuthMenuOpen(false);
      }
    };
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsAuthMenuOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isAuthMenuOpen]);

  if (showAuthenticatedLayout) {
    return (
      <div className="min-h-screen bg-neutral-100 flex">
        {/* Sidebar for desktop */}
        {showSidebar && <aside
          className={`hidden md:flex fixed top-0 left-0 flex-col bg-white border-r border-neutral-200 shadow-sm transition-all duration-300 z-40 ${
            isSidebarExpanded ? 'w-64' : 'w-20'
          } h-screen`}
        >
          <div className="flex items-center justify-center h-20 px-3 border-b border-neutral-200">
            <Link to={ROUTES.home} className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-lg flex items-center justify-center bg-primary/10">
                <img
                  src="/tulogo.png"
                  alt="trAInedup.ai logo"
                  className="h-12 w-auto"
                  onError={(e) => {
                    const target = e.currentTarget as HTMLImageElement;
                    target.onerror = null;
                    target.src = '/logo192.png';
                  }}
                />
              </div>
              {isSidebarExpanded && (
                <span className="font-montserrat font-extrabold text-lg" style={{ color: '#1e1e2e', letterSpacing: '-0.02em' }}>
                  tr<span style={{ color: '#5b6cff' }}>AI</span>ned up
                </span>
              )}
            </Link>
          </div>

          <nav className={`flex-1 px-2 py-4 space-y-1 ${isSidebarExpanded ? 'overflow-y-auto' : 'overflow-visible'}`}>
            <button
              type="button"
              onClick={() => {
                setIsSidebarExpanded((prev) => !prev);
                setOpenFlyoutItem(null);
                setExpandedSections({});
              }}
              className={`flex items-center w-full px-3 py-2 rounded-lg text-sm font-medium transition-colors duration-200 ${
                isSidebarExpanded
                  ? 'text-neutral-600 hover:bg-neutral-50 hover:text-neutral-900'
                  : 'justify-center text-neutral-600 hover:bg-neutral-50 hover:text-neutral-900'
              }`}
              aria-label="Toggle navigation"
            >
              <Menu className="w-5 h-5" />
              {isSidebarExpanded && <span className="ml-3 flex-1 text-left">Toggle Menu</span>}
            </button>

            {authenticatedNavItems.map((item) => {
              const Icon = item.icon;
              const isActive = item.href ? isActiveRoute(item.href, item.children) : false;
              const hasChildren = Boolean(item.children);
              const displayName = isSidebarExpanded && item.expandedLabel ? item.expandedLabel : item.name;
              const isFlyoutOpen = openFlyoutItem === item.name;

              const handleNavItemClick = () => {
                if (item.onClick) {
                  item.onClick();
                  setOpenFlyoutItem(null);
                  return;
                }

                if (hasChildren) {
                  if (isSidebarExpanded) {
                    toggleSection(item.name);
                  } else {
                    setOpenFlyoutItem((prev) => (prev === item.name ? null : item.name));
                  }
                } else if (item.href) {
                  navigate(item.href);
                  setOpenFlyoutItem(null);
                }
              };

              return (
                <div key={item.name} className="relative">
                  <button
                    type="button"
                    onClick={handleNavItemClick}
                    className={`flex items-center w-full px-3 py-2 rounded-lg text-sm font-medium transition-colors duration-200 ${
                      isActive
                        ? 'bg-primary-50 text-primary border border-primary/20'
                        : item.highlight
                        ? 'bg-red-50 text-red-700 border border-red-200 hover:bg-red-100'
                        : 'text-neutral-600 hover:bg-neutral-50 hover:text-neutral-900'
                    } ${!isSidebarExpanded ? 'justify-center' : ''}`}
                    title={!isSidebarExpanded ? displayName : undefined}
                  >
                    <span className="relative">
                      <Icon className="w-5 h-5" />
                      {item.highlight && (
                        <span className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-red-500 rounded-full border-2 border-white" />
                      )}
                    </span>
                    {isSidebarExpanded && (
                      <span className="ml-3 flex-1 text-left flex items-center gap-2">
                        {displayName}
                        {item.highlight && (
                          <span className="inline-flex items-center px-1.5 py-0.5 text-[10px] font-bold rounded-full bg-red-500 text-white leading-none">
                            Due
                          </span>
                        )}
                      </span>
                    )}
                    {hasChildren && isSidebarExpanded && (
                      <ChevronDown
                        className={`w-4 h-4 transition-transform ${
                          expandedSections[item.name] ? 'rotate-180' : ''
                        }`}
                      />
                    )}
                  </button>

                  {hasChildren && isSidebarExpanded && expandedSections[item.name] && (
                    <div className="mt-1 ml-6 space-y-1">
                      {item.children?.map((child) => (
                        <Link
                          key={child.name}
                          to={child.href}
                          className={`block px-3 py-2 rounded-md text-sm transition-colors duration-200 ${
                            isActiveRoute(child.href)
                              ? 'bg-primary-50 text-primary'
                              : 'text-neutral-600 hover:bg-neutral-50 hover:text-neutral-900'
                          }`}
                          onClick={() => setExpandedSections((prev) => ({ ...prev, [item.name]: true }))}
                        >
                          {child.name}
                        </Link>
                      ))}
                    </div>
                  )}

                  {hasChildren && !isSidebarExpanded && (
                  <div
                    className={`absolute top-0 left-full ml-2 bg-white border border-neutral-200 rounded-lg shadow-lg py-2 min-w-[180px] z-50 max-h-[calc(100vh-1rem)] overflow-y-auto ${
                      isFlyoutOpen ? 'block' : 'hidden'
                    }`}
                  >
                      {item.children?.map((child) => (
                        <Link
                          key={child.name}
                          to={child.href}
                          className={`flex items-center px-4 py-2 text-sm transition-colors duration-200 ${
                            isActiveRoute(child.href)
                              ? 'text-primary bg-primary-50'
                              : 'text-neutral-700 hover:bg-neutral-100'
                          }`}
                          onClick={() => setOpenFlyoutItem(null)}
                        >
                          {child.name}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </nav>

          {/* User avatar section at bottom of sidebar */}
          <div className="border-t border-neutral-200 p-3 relative" ref={authMenuRef}>
            <button
              type="button"
              onClick={() => setIsAuthMenuOpen((prev) => !prev)}
              className={`flex items-center w-full rounded-lg px-2 py-2 transition-colors duration-200 hover:bg-neutral-50 ${!isSidebarExpanded ? 'justify-center' : 'gap-3'}`}
              aria-label="Open user menu"
            >
              <div
                className="w-8 h-8 rounded-full flex items-center justify-center font-montserrat font-bold text-sm text-white flex-shrink-0"
                style={{ background: 'linear-gradient(135deg, #5b6cff, #7c8eff)' }}
              >
                {userInitials}
              </div>
              {isSidebarExpanded && (
                <div className="flex-1 min-w-0 text-left">
                  <p className="font-montserrat font-bold text-xs text-neutral-800 truncate">
                    {user?.displayName || [user?.firstName, user?.lastName].filter(Boolean).join(' ') || 'User'}
                  </p>
                  <p className="font-open-sans text-xs text-neutral-500 truncate">{user?.email}</p>
                </div>
              )}
            </button>
            {isAuthMenuOpen && renderAuthAvatarDropdown('above')}
          </div>
        </aside>}

        {/* Main content area */}
        <div
          className={`flex-1 flex flex-col min-h-screen ${desktopContentOffset}`}
          style={{
            paddingTop: contentPaddingTop,
            ...headerOffsetStyle,
          }}
        >
          <header
            ref={headerRef}
            className="fixed md:hidden top-0 z-40 bg-white border-b border-neutral-200 w-full"
          >
            <div className="relative grid grid-cols-[auto,1fr,auto] items-center px-4 py-3">
              <div className="flex items-center justify-start space-x-3">
                <button
                  onClick={() => setIsMobileMenuOpen((prev) => !prev)}
                  className="p-2 rounded-lg hover:bg-neutral-100 transition-colors md:hidden"
                  aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
                >
                  {isMobileMenuOpen ? (
                    <X className="w-6 h-6 text-neutral-600" />
                  ) : (
                    <Menu className="w-6 h-6 text-neutral-600" />
                  )}
                </button>
              </div>

              <div className="flex items-center justify-center md:justify-start">
                <Link to={ROUTES.home} className="flex items-center gap-2 md:hidden">
                  <img
                    src="/tulogo.png"
                    alt="trAInedup.ai logo"
                    className="h-6 w-auto"
                    onError={(e) => {
                      const target = e.currentTarget as HTMLImageElement;
                      target.onerror = null;
                      target.src = '/logo192.png';
                    }}
                  />
                  <span className="font-montserrat font-semibold text-neutral-800">trAInedup.ai</span>
                </Link>

                {isAuthenticated && gamificationProgress && (
                  <div className="hidden md:block">
                    <NextStepBlock
                      completedTasks={gamificationProgress.completedTasks}
                      readinessPercentage={gamificationProgress.readinessPercentage}
                      nextTask={gamificationProgress.nextTask}
                    />
                  </div>
                )}
              </div>

              <div className="flex items-center justify-end gap-2">
                {isAuthenticated && !isAffiliateDashboard && (
                  <XPCounter
                    totalXP={totalXP}
                    readinessPercentage={readinessPercentage}
                    isExpanded={!isReadinessCollapsed}
                    onToggle={handleReadinessToggle}
                  />
                )}
                {SHOW_LANGUAGE_SWITCHER && <LanguageSwitcher />}
                {/* Mobile avatar + dropdown */}
                {isAuthenticated && (
                  <div className="relative" ref={authMenuRef}>
                    <button
                      type="button"
                      onClick={() => setIsAuthMenuOpen((prev) => !prev)}
                      className="w-8 h-8 rounded-full flex items-center justify-center font-montserrat font-bold text-sm text-white flex-shrink-0 hover:opacity-90 transition-opacity"
                      style={{ background: 'linear-gradient(135deg, #5b6cff, #7c8eff)' }}
                      aria-label="Open user menu"
                    >
                      {userInitials}
                    </button>
                    {isAuthMenuOpen && renderAuthAvatarDropdown('below')}
                  </div>
                )}
              </div>
            </div>

            {/* Mobile Navigation Menu */}
            {isMobileMenuOpen && (
              <div className="md:hidden border-t border-neutral-200 py-3 bg-white">
                <nav className="flex flex-col px-4 space-y-2">
                  {authenticatedNavItems.map((item) => {
                    const Icon = item.icon;
                    const mobileLabel = item.expandedLabel ?? item.name;
                    const hasChildren = Boolean(item.children);
                    const isExpanded = expandedMobileSections[item.name];

                    if (!hasChildren) {
                      const itemContent = (
                        <>
                          <span className="relative">
                            <Icon className="w-5 h-5" />
                            {item.highlight && (
                              <span className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-red-500 rounded-full border-2 border-white" />
                            )}
                          </span>
                          <span className="flex items-center gap-2">
                            {mobileLabel}
                            {item.highlight && (
                              <span className="inline-flex items-center px-1.5 py-0.5 text-[10px] font-bold rounded-full bg-red-500 text-white leading-none">
                                Due
                              </span>
                            )}
                          </span>
                        </>
                      );

                      if (item.onClick) {
                        return (
                          <button
                            key={item.name}
                            type="button"
                            className="flex items-center gap-3 font-medium rounded-lg px-3 py-2 text-left text-neutral-700 hover:bg-neutral-50 hover:text-primary"
                            onClick={() => {
                              setIsMobileMenuOpen(false);
                              item.onClick?.();
                            }}
                          >
                            {itemContent}
                          </button>
                        );
                      }

                      return (
                        <Link
                          key={item.name}
                          to={item.href ?? '#'}
                          className={`flex items-center gap-3 font-medium rounded-lg px-3 py-2 transition-colors duration-200 ${
                            item.href && isActiveRoute(item.href, item.children)
                              ? 'text-primary bg-primary-50'
                              : item.highlight
                              ? 'bg-red-50 text-red-700 border border-red-200 hover:bg-red-100'
                              : 'text-neutral-700 hover:bg-neutral-50 hover:text-primary'
                          }`}
                          onClick={() => setIsMobileMenuOpen(false)}
                        >
                          {itemContent}
                        </Link>
                      );
                    }

                    return (
                      <div key={item.name} className="flex flex-col">
                        <button
                          type="button"
                          onClick={() => toggleMobileSection(item.name)}
                          className="flex items-center justify-between w-full gap-3 font-medium rounded-lg px-3 py-2 text-left text-neutral-700 hover:bg-neutral-50"
                          aria-expanded={isExpanded}
                        >
                          <span className="flex items-center gap-3">
                            <Icon className="w-5 h-5" />
                            <span>{mobileLabel}</span>
                          </span>
                          <ChevronDown
                            className={`w-4 h-4 transition-transform ${isExpanded ? 'rotate-180 text-primary' : 'text-neutral-500'}`}
                          />
                        </button>

                        {isExpanded && (
                          <div className="flex flex-col space-y-1 pl-9">
                            {item.children?.map((child) => (
                              <Link
                                key={child.name}
                                to={child.href}
                                className={`flex items-center gap-2 rounded-lg px-3 py-2 text-sm transition-colors duration-200 ${
                                  isActiveRoute(child.href)
                                    ? 'text-primary bg-primary-50'
                                    : 'text-neutral-700 hover:bg-neutral-50 hover:text-primary'
                                }`}
                                onClick={() => setIsMobileMenuOpen(false)}
                              >
                                {child.name}
                              </Link>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </nav>
              </div>
            )}
          </header>

          {/* Floating XP Widget for Desktop */}
          {isAuthenticated && isXPWidgetVisible && !isAffiliateDashboard && (
            <div className="hidden md:flex fixed top-6 right-6 z-50 flex-col items-end gap-2 animate-in fade-in slide-in-from-top-4 duration-500">
              <div className="relative group">
                <XPCounter
                  totalXP={totalXP}
                  readinessPercentage={readinessPercentage}
                  isExpanded={!isReadinessCollapsed}
                  onToggle={handleReadinessToggle}
                />
                <button
                  onClick={handleCloseXPWidget}
                  className="absolute -top-2 -right-2 bg-white text-neutral-400 hover:text-neutral-600 rounded-full p-0.5 shadow-sm border border-neutral-200 transition-colors"
                  aria-label="Hide XP Widget"
                >
                  <X className="w-3 h-3" />
                </button>
              </div>

              {/* Expanded Widget Content */}
              {!isReadinessCollapsed && (
                <div className="animate-in fade-in slide-in-from-top-2 duration-200 origin-top-right">
                  <ReadinessBarWrapper
                    isCollapsed={false}
                    onToggleCollapse={handleReadinessToggle}
                    disableCollapse={false}
                    variant="widget"
                  />
                </div>
              )}
            </div>
          )}

          {/* Upgrade Banner for Free Users */}
          {showUpgradeBanner && (
            <div className="hidden">
              <UpgradeBanner />
            </div>
          )}

          {/* AI Readiness Bar (Mobile or Readiness Page) */}
          {showReadinessBar && !isDesktopViewport && !isReadinessPage && (
            <div
              className={`bg-white shadow-sm ${
                isReadinessPage ? 'sticky top-[var(--header-offset,64px)] z-30 md:top-0' : ''
              }`}
              style={headerOffsetStyle}
            >
              <ReadinessBarWrapper
                isCollapsed={isReadinessCollapsed}
                onToggleCollapse={handleReadinessToggle}
                disableCollapse={false}
                isMobile={true}
              />
            </div>
          )}

          <main className="flex-1">{children}</main>

          {/* Footer */}
          {showFooter && (
            <footer className="bg-white border-t border-neutral-200 mt-auto">
              <div className="container-custom py-12">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
                  {/* Company Info */}
                  <div className="col-span-1 md:col-span-2">
                    <Link to={ROUTES.home} className="flex items-center space-x-3 mb-4">
                      <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-primary/10">
                        <img
                          src="/tulogo.png"
                          alt="trAInedup.ai logo"
                          className="h-10 w-auto"
                          onError={(e) => {
                            const target = e.currentTarget as HTMLImageElement;
                            target.onerror = null;
                            target.src = '/logo192.png';
                          }}
                        />
                      </div>
                      <span className="font-montserrat font-bold text-xl text-neutral-800">
                        trAInedup.ai
                      </span>
                    </Link>
                    <p className="text-neutral-600 mb-4 max-w-md">
                      Helping busy professionals, freelancers, and SMEs who feel left behind by AI
                      catch up with practical skills through personalised learning experiences.
                    </p>
                    {/* Social icons */}
                    <div className="flex items-center space-x-4 mb-4">
                      <a
                        href="https://www.instagram.com/trained.up/"
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label="Instagram"
                        className="text-neutral-500 hover:text-primary transition-colors duration-200"
                      >
                        <Instagram className="w-5 h-5" />
                      </a>
                      <a
                        href="https://www.facebook.com/trained.up/"
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label="Facebook"
                        className="text-neutral-500 hover:text-primary transition-colors duration-200"
                      >
                        <Facebook className="w-5 h-5" />
                      </a>
                      <a
                        href="https://discord.com/invite/WUHyEZHnS9"
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label="Discord"
                        className="text-neutral-500 hover:text-primary transition-colors duration-200"
                      >
                        <MessageCircle className="w-5 h-5" />
                      </a>
                      <a
                        href="https://www.youtube.com/@trAIned_up"
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label="YouTube"
                        className="text-neutral-500 hover:text-primary transition-colors duration-200"
                      >
                        <Youtube className="w-5 h-5" />
                      </a>
                    </div>
                    <p className="text-sm text-neutral-500">
                      © 2025 trAInedup.ai. All rights reserved.
                    </p>
                  </div>

                  {/* Quick Links */}
                  <div>
                    <h3 className="font-montserrat font-semibold text-neutral-800 mb-4">
                      Quick Links
                    </h3>
                    <ul className="space-y-2">
                      <li>
                        <Link to={ROUTES.about} className="text-neutral-600 hover:text-primary transition-colors duration-200">
                          About Us
                        </Link>
                      </li>
                      <li>
                        <Link to={ROUTES.pricing} className="text-neutral-600 hover:text-primary transition-colors duration-200">
                          Pricing
                        </Link>
                      </li>
                      <li>
                        <Link to={ROUTES.courseCatalog} className="text-neutral-600 hover:text-primary transition-colors duration-200">
                          All Courses
                        </Link>
                      </li>
                      <li>
                        <Link to={ROUTES.blog} className="text-neutral-600 hover:text-primary transition-colors duration-200">
                          Blog
                        </Link>
                      </li>
                      <li>
                        <Link to={ROUTES.contact} className="text-neutral-600 hover:text-primary transition-colors duration-200">
                          Contact
                        </Link>
                      </li>
                    </ul>
                  </div>

                  {/* Support */}
                  <div>
                    <h3 className="font-montserrat font-semibold text-neutral-800 mb-4">
                      Support
                    </h3>
                    <ul className="space-y-2">
                      <li>
                        <button className="text-neutral-600 hover:text-primary transition-colors duration-200 text-left">
                          Help Center
                        </button>
                      </li>
                      <li>
                        <Link to="/privacy" className="text-neutral-600 hover:text-primary transition-colors duration-200">
                          Privacy Policy
                        </Link>
                      </li>
                      <li>
                        <Link to="/terms" className="text-neutral-600 hover:text-primary transition-colors duration-200">
                          Terms of Service
                        </Link>
                      </li>
                      <li>
                        <Link to="/unsubscribe" className="text-neutral-600 hover:text-primary transition-colors duration-200">
                          Unsubscribe
                        </Link>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </footer>
          )}
        </div>

        {/* Subscription recommendation chatbot for visitors */}
        {showChatbot && <SubscriptionChatbot />}
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-100 flex flex-col">
      {/* Header */}
      {showHeader && (
        <header className="bg-white/90 backdrop-blur-md border-b border-neutral-100 sticky top-0 z-50 shadow-sm">
          <div className="container-custom">
            <div className="flex justify-between items-center h-16">
              {/* Logo */}
              <Link to={ROUTES.home} className="flex items-center space-x-2">
                <div className="w-14 h-14 rounded-lg flex items-center justify-center">
                  <img
                    src="/tulogo.png"
                    alt="trAInedup.ai logo"
                    className="h-14 w-auto"
                    onError={(e) => {
                      const target = e.currentTarget as HTMLImageElement;
                      target.onerror = null;
                      target.src = '/logo192.png';
                    }}
                  />
                </div>
                <span className="font-montserrat font-bold text-xl text-neutral-800">
                  tr<span style={{ color: '#5b6cff' }}>AI</span>ned up
                </span>
              </Link>

              {/* Desktop Navigation */}
              <nav className="hidden md:flex items-center space-x-8">
                {navItems.map((item) => {
                  if (!item.href) return null;

                  return (
                    <Link
                      key={item.name}
                      to={item.href}
                      className={`font-montserrat font-medium text-sm transition-colors duration-200 ${
                        isActiveRoute(item.href, item.children)
                          ? 'text-primary border-b-2 border-primary pb-1'
                          : 'text-neutral-600 hover:text-primary'
                      }`}
                    >
                      {item.name}
                    </Link>
                  );
                })}
              </nav>

              {/* Desktop Auth Buttons / User Menu + Language Switcher */}
              <div className="hidden md:flex items-center space-x-3">
                {/* Language Switcher on the right (temporarily hidden) */}
                {SHOW_LANGUAGE_SWITCHER && <LanguageSwitcher />}
                {showAuthenticatedLayout ? (
                  <Button variant="ghost" size="sm" onClick={handleSignOut}>
                    Sign Out
                  </Button>
                ) : (
                  <div className="flex items-center space-x-3">
                    <Link
                      to={ROUTES.login}
                      className="font-montserrat font-medium text-sm text-neutral-600 hover:text-primary transition-colors"
                    >
                      Sign In
                    </Link>
                    <Link
                      to={ROUTES.register}
                      className="px-5 py-2 rounded-full text-white font-montserrat font-semibold text-sm transition-opacity hover:opacity-90 shadow-md"
                      style={{ background: '#5b6cff', boxShadow: '0 2px 12px rgba(91,108,255,0.35)' }}
                    >
                      Get Started Free
                    </Link>
                  </div>
                )}
              </div>

              {/* Mobile Controls */}
              <div className="flex items-center space-x-2 md:hidden">
                <button
                  onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                  className="p-2 rounded-lg hover:bg-neutral-100 transition-colors duration-200"
                  aria-label="Toggle navigation menu"
                >
                  {isMobileMenuOpen ? (
                    <X className="w-6 h-6 text-neutral-600" />
                  ) : (
                    <Menu className="w-6 h-6 text-neutral-600" />
                  )}
                </button>

                {showAuthenticatedLayout && (
                  <div className="relative">
                    <button
                      onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                      className="p-2 rounded-full border border-neutral-200 hover:bg-neutral-100 transition-colors duration-200"
                      aria-label="Open user menu"
                    >
                      <User className="w-5 h-5 text-neutral-700" />
                    </button>
                    {isUserMenuOpen && renderUserMenuDropdown()}
                  </div>
                )}
              </div>
            </div>

            {/* Mobile Navigation Menu */}
            {isMobileMenuOpen && (
              <div className="md:hidden border-t border-neutral-200 py-4">
                <nav className="flex flex-col space-y-4">
                  {navItems.map((item) => {
                    if (!item.href) return null;

                    return (
                      <Link
                        key={item.name}
                        to={item.href}
                        className={`font-medium transition-colors duration-200 ${
                          isActiveRoute(item.href, item.children)
                            ? 'text-primary'
                            : 'text-neutral-600 hover:text-primary'
                        }`}
                        onClick={() => setIsMobileMenuOpen(false)}
                      >
                        {item.name}
                      </Link>
                    );
                  })}

                  {/* Mobile Language Switcher (temporarily hidden) */}
                  {SHOW_LANGUAGE_SWITCHER && (
                    <div className="flex flex-col space-y-2 pt-4 border-t border-neutral-200">
                      <LanguageSwitcher />
                    </div>
                  )}
                   {/* Mobile Auth Buttons */}
                   {!showAuthenticatedLayout && (
                     <div className="flex flex-col space-y-2 pt-4 border-t border-neutral-200">
                       <Link to={ROUTES.login} onClick={() => setIsMobileMenuOpen(false)}>
                         <Button variant="ghost" size="sm" fullWidth>
                           Sign In
                         </Button>
                       </Link>
                       <Link
                         to={ROUTES.register}
                         onClick={() => setIsMobileMenuOpen(false)}
                         className="block w-full py-2.5 px-6 rounded-full text-white font-montserrat font-semibold text-sm text-center transition-opacity hover:opacity-90"
                         style={{ background: '#5b6cff', boxShadow: '0 2px 12px rgba(91,108,255,0.35)' }}
                       >
                         Get Started Free
                       </Link>
                     </div>
                   )}
                 </nav>
               </div>
             )}
          </div>
        </header>
      )}

      {/* Upgrade Banner for Free Users */}
      {showUpgradeBanner && <UpgradeBanner />}

      {/* AI Readiness Bar */}
      <ReadinessBarWrapper />

      {/* Main Content */}
      <main className="flex-1">
        {children}
      </main>

      {/* Footer */}
      {showFooter && (
        <footer className="bg-white border-t border-neutral-100 mt-auto">
          <div className="container-custom py-10">
            {/* Top row: logo + nav links */}
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6 mb-8">
              {/* Logo */}
              <Link to={ROUTES.home} className="flex items-center space-x-2">
                <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-primary/10">
                  <img
                    src="/tulogo.png"
                    alt="trAInedup.ai logo"
                    className="h-10 w-auto"
                    onError={(e) => {
                      const target = e.currentTarget as HTMLImageElement;
                      target.onerror = null;
                      target.src = '/logo192.png';
                    }}
                  />
                </div>
                <span className="font-montserrat font-bold text-lg text-neutral-800">
                  tr<span style={{ color: '#5b6cff' }}>AI</span>ned up
                </span>
              </Link>

              {/* Nav links */}
              <nav className="flex flex-wrap gap-x-6 gap-y-2">
                {[
                  { label: 'About', to: ROUTES.about },
                  { label: 'Pricing', to: ROUTES.pricing },
                  { label: 'Courses', to: ROUTES.courses },
                  { label: 'All Courses', to: ROUTES.courseCatalog },
                  { label: 'Blog', to: ROUTES.blog },
                  { label: 'Contact', to: ROUTES.contact },
                  { label: 'Privacy Policy', to: '/privacy' },
                  { label: 'Terms of Service', to: '/terms' },
                ].map(({ label, to }) => (
                  <Link
                    key={label}
                    to={to}
                    className="font-open-sans text-sm text-neutral-500 hover:text-primary transition-colors duration-200"
                  >
                    {label}
                  </Link>
                ))}
              </nav>
            </div>

            {/* Divider */}
            <div className="border-t border-neutral-100 pt-6">
              <p className="font-open-sans text-sm text-neutral-400 text-center md:text-left">
                © {new Date().getFullYear()} trAInedup.ai. All rights reserved.
              </p>
            </div>
          </div>
        </footer>
      )}
      {/* Subscription recommendation chatbot for visitors */}
      {showChatbot && <SubscriptionChatbot />}
    </div>
  );
};
